import React, { useState } from "react";

import NameIcon from "@mui/icons-material/Badge";
import CollapseIcon from "@mui/icons-material/KeyboardArrowRight";
import AltitudeIcon from "@mui/icons-material/Height";
import OrganizationIcon from "@mui/icons-material/CorporateFare";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";

import { Collapse, Fab, Typography, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router";
import { useUserAuth } from "../../contexts/authContext";
import { MapDrawComponent } from "../../map/mapDrawComponent";
import { ConvertCoordinatesToVertices, ConvertFeetToMeters } from "../../util";
import { useMap } from "../../contexts/mapContext";

export default function COACreator() {
    const [geoJson, setGeoJson] = useState(null);
    const [name, setName] = useState("");
    const [nameError, setNameError] = useState("");
    const [altitudeFt, setAltitudeFt] = useState(400);
    const [altitudeError, setAltitudeError] = useState("");

    const [submitting, setSubmitting] = useState(false);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

    const { user, getOrganizationByID, setSnackbar, handleFailedFetch } = useUserAuth();
    const { coaVolumes, publishedOperations } = useMap();

    const isDesktop = useMediaQuery("(min-width:900px)");
    const navigate = useNavigate();

    const checkInputFieldsAreValid = () => {
        const prechecks = [
            {
                passed: geoJson && geoJson.length,
                snackbar: "Please create a valid COA volume to continue."
            },
            {
                passed: name && name.trim(),
                errorMessage: "Please enter a valid name.",
                setErrorMessage: setNameError
            },
            {
                passed: parseFloat(altitudeFt) > 0,
                errorMessage: "Please enter a valid altitude.",
                setErrorMessage: setAltitudeError
            }
        ];
        for (const precheck of prechecks) {
            if (precheck.passed) {
                if (precheck.setErrorMessage) {
                    precheck.setErrorMessage("");
                }
                continue;
            } else if (precheck.snackbar) {
                setSnackbar({ children: precheck.snackbar, severity: "error" });
            } else if (precheck.errorMessage && precheck.setErrorMessage) {
                precheck.setErrorMessage(precheck.errorMessage);
            }
            setSubmitting(false);
            return false;
        }
        return true;
    };

    const handlePublish = () => {
        const validFields = checkInputFieldsAreValid();
        if (!validFields) {
            return;
        }

        const polygons = [];
        geoJson.forEach((coords) => {
            const polygon = {
                type: "polygon",
                vertices: ConvertCoordinatesToVertices(coords)
            };
            polygons.push(polygon);
        });

        const alertVolume = {
            name: name,
            altitude_agl_m: ConvertFeetToMeters(parseFloat(altitudeFt)),
            organization_id: user.organization_id,
            polygons,
            date_created: new Date().toISOString(),
            deleted: false
        };
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(alertVolume)
        };
        fetch("/api/coaVolume/insert", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => handleClose())
            .catch((err) => handleFailedFetch(err));
    };

    const handleClose = () => {
        navigate("/");
    };

    return (
        <Box style={{ position: "relative", width: "100%", height: "100%", display: "flex" }}>
            <Collapse in={isDesktop || mobileDrawerOpen} orientation="horizontal">
                <Box sx={{ p: 2, width: { xs: "100%", md: "375px" }, height: "100%", overflowY: "auto" }}>
                    <Button
                        endIcon={<CollapseIcon />}
                        onClick={() => setMobileDrawerOpen(false)}
                        sx={{ display: isDesktop ? "none" : "flex", flexDirection: "row", marginLeft: "auto" }}
                    >
                        Back to Map
                    </Button>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, overflowY: "auto", flexShrink: 0 }}>
                        <Typography variant="body1">COA Volume Details</Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <IconField icon={NameIcon} info={["COA Name", name, setName, nameError]} />
                            <IconField icon={AltitudeIcon} info={["Altitude (AGL)", altitudeFt, setAltitudeFt, altitudeError]} units="ft" />
                            <IconField icon={OrganizationIcon} info={["Organization", getOrganizationByID(user.organization_id).name]} disabled />
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Button fullWidth variant="contained" loading={submitting} onClick={handlePublish} id="Publish">
                                Publish
                            </Button>
                            <Button fullWidth variant="text" onClick={handleClose}>
                                Close
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Collapse>

            <Box sx={{ flex: 1, position: "relative", overflow: "hidden" }}>
                <MapDrawComponent
                    type="COA"
                    coa={coaVolumes}
                    publishedFlights={publishedOperations}
                    changeDrawType={() => {}}
                    canEdit
                    volumeGeojson={geoJson}
                    setVolumeGeojson={setGeoJson}
                    setName={setName}
                    setMinAltitude={() => {}}
                    setMaxAltitude={setAltitudeFt}
                    setMobileDrawerOpen={setMobileDrawerOpen}
                />
                <Fab
                    size="medium"
                    color="primary"
                    onClick={() => setMobileDrawerOpen(true)}
                    sx={{
                        display: isDesktop || mobileDrawerOpen ? "none" : "block",
                        position: "absolute",
                        bottom: 8,
                        left: 8
                    }}
                >
                    <CollapseIcon />
                </Fab>
            </Box>
        </Box>
    );
}

const IconField = ({ icon, info, children, ...props }) => (
    <IconWrapper icon={icon}>
        <InfoField info={info} {...props}>
            {children}
        </InfoField>
    </IconWrapper>
);

const IconWrapper = ({ icon: Icon, children }) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Icon fontSize="small" />
        {children}
    </Box>
);

const InfoField = ({ info, units, children, ...props }) => (
    <TextField
        id={info?.[0]}
        fullWidth
        size="small"
        margin="dense"
        label={info?.[0]}
        value={info?.[1]}
        onChange={(e) => info[2](e.target.value)}
        error={!!info?.[3]}
        helperText={info?.[3]}
        type={units ? "number" : undefined}
        onWheel={(e) => e.target.blur()}
        InputProps={{ endAdornment: units ? <InputAdornment position="end">{units}</InputAdornment> : undefined }}
        inputProps={{ "data-testid": info?.[0] }}
        {...props}
    >
        {children}
    </TextField>
);
